/**
 * Offline Indicator Components
 *
 * Visual indicators for connection status and sync state.
 * Implements RFP requirements for:
 * - Offline status visibility
 * - Sync progress indication
 * - Pending action queue visibility
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOffline, SyncStatus } from '@/hooks/useOffline';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// OFFLINE BANNER
// ============================================================================

export function OfflineBanner() {
  const { t } = useTranslation();
  const { isOnline, syncStatus } = useOffline();
  const [isVisible, setIsVisible] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Track offline state changes
  useEffect(() => {
    if (!isOnline) {
      setIsVisible(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "back online" message briefly
      setTimeout(() => {
        setIsVisible(false);
        setWasOffline(false);
      }, 3000);
    }
  }, [isOnline, wasOffline]);

  if (!isVisible && isOnline) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] px-4 py-2 text-center text-sm font-medium transition-all duration-300 ${
        isOnline
          ? 'bg-green-500 text-white'
          : 'bg-amber-500 text-amber-950'
      }`}
      role="status"
      aria-live="polite"
    >
      {isOnline ? (
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg" aria-hidden="true">✓</span>
          {t('offline.backOnline', 'Back online!')}
          {syncStatus.pendingCount > 0 && (
            <span>
              {t('offline.syncing', 'Syncing {{count}} items...', {
                count: syncStatus.pendingCount,
              })}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg" aria-hidden="true">⚠️</span>
          {t('offline.offline', "You're offline")}
          {syncStatus.pendingCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {syncStatus.pendingCount} {t('offline.pendingItems', 'pending')}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SYNC STATUS INDICATOR
// ============================================================================

interface SyncIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

export function SyncIndicator({ showDetails = false, className = '' }: SyncIndicatorProps) {
  const { t, i18n } = useTranslation();
  const { fmt } = useFormattedDate();
  const { isOnline, syncStatus, syncNow } = useOffline();
  const locale = i18n?.language || 'en';

  const formatLastSync = (date: Date | null): string => {
    if (!date) return t('offline.neverSynced', 'Never');

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('offline.justNow', 'Just now');
    if (diffMins < 60) {
      return locale === 'es'
        ? `hace ${diffMins} min`
        : `${diffMins} min ago`;
    }

    return fmt.time(date);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Connection status dot */}
      <span
        className={`w-2 h-2 rounded-full ${
          isOnline ? 'bg-green-500' : 'bg-red-500'
        }`}
        title={isOnline ? t('offline.online', 'Online') : t('offline.offline', 'Offline')}
      />

      {/* Syncing spinner */}
      {syncStatus.isSyncing && (
        <div
          className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"
          aria-label={t('offline.syncing', 'Syncing...')}
        />
      )}

      {showDetails && (
        <>
          {/* Last sync time */}
          <span className="text-xs text-muted-foreground">
            {t('offline.lastSync', 'Last sync')}: {formatLastSync(syncStatus.lastSyncAt)}
          </span>

          {/* Pending count */}
          {syncStatus.pendingCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {syncStatus.pendingCount} {t('offline.pending', 'pending')}
            </Badge>
          )}

          {/* Sync button */}
          {isOnline && syncStatus.pendingCount > 0 && !syncStatus.isSyncing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={syncNow}
              className="text-xs"
            >
              {t('offline.syncNow', 'Sync')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// SYNC PROGRESS MODAL
// ============================================================================

interface SyncProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SyncProgressModal({ isOpen, onClose }: SyncProgressModalProps) {
  const { t } = useTranslation();
  const { fmt } = useFormattedDate();
  const { syncStatus, syncNow } = useOffline();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">
          {t('offline.syncStatus', 'Sync Status')}
        </h3>

        <div className="space-y-4">
          {/* Connection status */}
          <div className="flex items-center justify-between">
            <span>{t('offline.connection', 'Connection')}</span>
            <Badge variant={syncStatus.isOnline ? 'default' : 'destructive'}>
              {syncStatus.isOnline
                ? t('offline.online', 'Online')
                : t('offline.offline', 'Offline')}
            </Badge>
          </div>

          {/* Pending items */}
          <div className="flex items-center justify-between">
            <span>{t('offline.pendingChanges', 'Pending Changes')}</span>
            <span className="font-medium">{syncStatus.pendingCount}</span>
          </div>

          {/* Sync progress */}
          {syncStatus.isSyncing && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">{t('offline.syncing', 'Syncing...')}</span>
              </div>
              <Progress value={50} className="h-2" />
            </div>
          )}

          {/* Error message */}
          {syncStatus.syncError && (
            <div className="p-3 bg-red-50 dark:bg-red-950 rounded text-sm text-red-600 dark:text-red-400">
              {syncStatus.syncError}
            </div>
          )}

          {/* Last sync */}
          {syncStatus.lastSyncAt && (
            <div className="text-xs text-muted-foreground">
              {t('offline.lastSyncAt', 'Last synced at {{time}}', {
                time: fmt.time(syncStatus.lastSyncAt),
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common.close', 'Close')}
          </Button>
          {syncStatus.isOnline && syncStatus.pendingCount > 0 && (
            <Button
              onClick={syncNow}
              disabled={syncStatus.isSyncing}
              className="flex-1"
            >
              {syncStatus.isSyncing
                ? t('offline.syncing', 'Syncing...')
                : t('offline.syncNow', 'Sync Now')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CACHE STATUS INDICATOR
// ============================================================================

interface CacheIndicatorProps {
  isFromCache: boolean;
  cachedAt: Date | null;
  onRefresh?: () => void;
  className?: string;
}

export function CacheIndicator({
  isFromCache,
  cachedAt,
  onRefresh,
  className = '',
}: CacheIndicatorProps) {
  const { t, i18n } = useTranslation();
  const { fmt } = useFormattedDate();
  const locale = i18n?.language || 'en';

  if (!isFromCache || !cachedAt) return null;

  const formatCacheTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return t('offline.justNow', 'just now');
    if (diffMins < 60) {
      return locale === 'es' ? `hace ${diffMins} min` : `${diffMins} min ago`;
    }
    if (diffHours < 24) {
      return locale === 'es' ? `hace ${diffHours} h` : `${diffHours}h ago`;
    }

    return fmt.medium(date);
  };

  return (
    <div
      className={`inline-flex items-center gap-2 text-xs text-muted-foreground ${className}`}
    >
      <span className="flex items-center gap-1">
        <span aria-hidden="true">📦</span>
        {t('offline.cachedData', 'Cached')} {formatCacheTime(cachedAt)}
      </span>
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="text-primary hover:underline"
        >
          {t('offline.refresh', 'Refresh')}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// OFFLINE-AWARE WRAPPER
// ============================================================================

interface OfflineAwareProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireOnline?: boolean;
}

export function OfflineAware({
  children,
  fallback,
  requireOnline = false,
}: OfflineAwareProps) {
  const { isOnline } = useOffline();

  if (requireOnline && !isOnline) {
    return (
      <>
        {fallback ?? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">📶</div>
            <p className="text-lg font-medium mb-2">
              {/* Using inline text since useTranslation can't be used conditionally */}
              You need to be online
            </p>
            <p className="text-sm text-muted-foreground">
              This feature requires an internet connection.
            </p>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}

export default OfflineBanner;
